
import { useState, useCallback } from "react";
import { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { useToast } from "@/hooks/use-toast";

export interface Objective {
  id: string;
  label: string;
  isCustom?: boolean;
}

const defaultObjectives: Objective[] = [
  { id: "engagement", label: "Engagement" },
  { id: "retention", label: "Retention" },
  { id: "growth", label: "Growth" },
  { id: "satisfaction", label: "Satisfaction" },
  { id: "adoption", label: "Adoption" },
  { id: "revenue", label: "Revenue" },
  { id: "reliability", label: "Reliability" },
  { id: "scalability", label: "Scalability" },
  { id: "conversions", label: "Conversions" },
  { id: "security", label: "Security" },
  { id: "loyalty", label: "Loyalty" },
];

export function useObjectives(
  initialSelected: string[],
  onSelectionChange: (objectives: string[]) => void
) {
  const [objectives, setObjectives] = useState<Objective[]>(defaultObjectives);
  const [newObjective, setNewObjective] = useState("");
  const { toast } = useToast();

  const handleObjectiveToggle = useCallback(
    (objectiveId: string) => {
      const isSelected = initialSelected.includes(objectiveId);
      
      if (isSelected) {
        // If already selected, remove it
        onSelectionChange(initialSelected.filter((id) => id !== objectiveId));
      } else {
        // If not selected and under limit, add it
        if (initialSelected.length >= 5) {
          return;
        }
        
        onSelectionChange([...initialSelected, objectiveId]);
      }
    },
    [initialSelected, onSelectionChange]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = initialSelected.indexOf(active.id.toString());
        const newIndex = initialSelected.indexOf(over.id.toString());
        
        const newOrder = arrayMove(initialSelected, oldIndex, newIndex);
        onSelectionChange(newOrder);
      }
    },
    [initialSelected, onSelectionChange]
  );

  const handleAddObjective = useCallback(() => {
    if (!newObjective.trim()) {
      toast({
        title: "Empty objective",
        description: "Please enter an objective name",
        variant: "destructive",
      });
      return;
    }

    // Check if objective with same label already exists
    const exists = objectives.some(
      (obj) => obj.label.toLowerCase() === newObjective.trim().toLowerCase()
    );

    if (exists) {
      toast({
        title: "Duplicate objective",
        description: "This objective already exists",
        variant: "destructive",
      });
      return;
    }

    const id = `custom-${Date.now()}`;
    const newObj: Objective = {
      id,
      label: newObjective.trim(),
      isCustom: true,
    };

    setObjectives((prev) => [...prev, newObj]);
    setNewObjective("");
    
    toast({
      title: "Objective added",
      description: `${newObjective.trim()} has been added to the list`,
    });
  }, [newObjective, objectives, toast]);

  return {
    objectives,
    newObjective,
    setNewObjective,
    handleObjectiveToggle,
    handleDragEnd,
    handleAddObjective,
  };
}
